import React, { createContext, useContext, useState, ReactNode } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { uniqBy, some } from "lodash";
import { NotificationType } from "@/utilities/types/Notification";

dayjs.extend(relativeTime);

export interface NotificationItem {
  id: string;
  title: ReactNode;
  message: ReactNode;
  icon?: React.ReactNode;
  timestamp: string;
  isRead: boolean;
  type: NotificationType;
  userId?: string;
  data?: any;
}

interface NotificationContextType {
  notifications: NotificationItem[];
  unreadCount: number;
  unreadCountApi: number;
  addNotification: (notification: Omit<NotificationItem, "isRead">) => void;
  pushDefaultNotifications: (notifications: NotificationItem[]) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  updateUnreadCountApi: (count: number) => void;
  minusOneReadCountApi: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCountApi, setUnreadCountApi] = useState<number>(0);

  const unreadCount = notifications.filter(
    (notification) => !notification.isRead,
  ).length;

  const addNotification = (notification: Omit<NotificationItem, "isRead">) => {
    const newNotification: NotificationItem = {
      ...notification,
      isRead: false,
    };

    setNotifications((prev) => {
      // Check if a notification with this ID already exists using lodash's some
      const exists = some(prev, { id: notification.id });
      // If it exists, don't add it again
      if (exists) return prev;
      // Otherwise add it to the beginning of the array
      return [newNotification, ...prev];
    });
  };

  const markAsRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id
          ? { ...notification, isRead: true }
          : notification,
      ),
    );
  };

  const markAllAsRead = () => {
    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, isRead: true })),
    );
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id),
    );
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  const pushDefaultNotifications = (notifications: NotificationItem[]) => {
    setNotifications((prev) => {
      // Use lodash to get unique notifications
      const combinedNotifications = [...prev, ...notifications];
      return uniqBy(combinedNotifications, "id");
    });
  };

  const updateUnreadCountApi = (count: number) => {
    setUnreadCountApi(count);
  };

  const minusOneReadCountApi = () => {
    setUnreadCountApi((pre) => (pre > 0 ? pre - 1 : 0));
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        unreadCountApi,
        addNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAllNotifications,
        pushDefaultNotifications,
        updateUnreadCountApi,
        minusOneReadCountApi,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
